// components/animations/Confetti.jsx
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const Confetti = ({ active = true, colors = [], count = 100 }) => {
  const canvasRef = useRef(null);
  const confettiCount = count;
  const confettiColors = colors.length > 0 
    ? colors 
    : ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
  
  useEffect(() => {
    if (!active || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Make the canvas full screen
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Confetti particles array
    const particles = [];
    
    // Create confetti particles
    for (let i = 0; i < confettiCount; i++) {
      particles.push({
        x: Math.random() * canvas.width, // x-position
        y: Math.random() * canvas.height - canvas.height, // y-position (start above the canvas)
        size: Math.random() * 10 + 5, // size
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)], // random color
        speed: Math.random() * 3 + 2, // falling speed
        angle: Math.random() * 2 * Math.PI, // random angle
        rotation: Math.random() * 0.2 - 0.1, // rotation speed
        swing: Math.random() * 0.1, // how much it swings
        swingAngle: Math.random() * 2 * Math.PI, // current swing angle
      });
    }
    
    // Animation function
    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw each particle
      particles.forEach((p) => {
        // Update position
        p.y += p.speed;
        p.x += Math.sin(p.swingAngle) * p.swing;
        p.swingAngle += 0.01;
        p.angle += p.rotation;
        
        // Draw particle (rectangle with rotation)
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
        
        // Reset particle if it goes offscreen
        if (p.y > canvas.height) {
          p.y = -p.size;
          p.x = Math.random() * canvas.width;
        }
      });
      
      // Continue animation if component is still mounted
      if (active) {
        requestAnimationFrame(animate);
      }
    };
    
    // Start animation
    const animationId = requestAnimationFrame(animate);
    
    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [active, confettiColors, confettiCount]);
  
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: active ? 'block' : 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default Confetti;